
import { useRef, useState, useEffect } from 'react';
import { Session, Message } from '@/lib/session';
import { toast } from '@/components/ui/use-toast';

export function useSession() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isNewConversation, setIsNewConversation] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSharedSession, setIsSharedSession] = useState(false);
  const sessionRef = useRef<Session>();

  const initSession = async () => {
    try {
      setIsInitializing(true);
      setError(null);

      const url = new URL(window.location.href);
      const urlSessionId = url.searchParams.get('session');
      
      sessionRef.current = new Session(urlSessionId || undefined);
      
      const pickupLine = await sessionRef.current.start();
      const existingMessages = await sessionRef.current.loadMessages();
      
      // Detect if this is a shared session with existing messages
      if (urlSessionId && existingMessages.length > 0) {
        setIsSharedSession(true);
      }
      
      setMessages(existingMessages);
      
      if (pickupLine) {
        setMessages([{ role: 'assistant', content: pickupLine }]);
      }
    } catch (error) {
      console.error('Error initializing chat:', error);
      setError('Failed to load chat session');
      const url = new URL(window.location.href);
      url.searchParams.delete('session');
      window.history.replaceState({}, '', url.toString());
    } finally {
      setIsInitializing(false);
    }
  };

  const sendMessage = async (userMessage: string) => {
    if (!sessionRef.current) return;
    
    setIsLoading(true);
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);

    try {
      setMessages(prev => [...prev, { role: 'assistant', content: '' }]);
      
      await sessionRef.current.sendMessage(userMessage, (message) => {
        setMessages(prev => [...prev.slice(0, -1), message]);
      });
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => prev.slice(0, -1));
      toast({
        title: "Failed to send message",
        description: "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = async () => {
    try {
      setIsNewConversation(true);
      sessionRef.current?.cleanup();
      sessionRef.current = new Session();
      const pickupLine = await sessionRef.current.start();
      const url = new URL(window.location.href);
      url.searchParams.delete('session');
      window.history.replaceState({}, '', url.toString());
      setMessages([]);
      setTimeout(() => {
        setMessages([{ role: 'assistant', content: pickupLine || '' }]);
        setIsNewConversation(false);
        toast({
          title: "New conversation started",
          description: "Starting a fresh conversation with a new session",
        });
      }, 2000);
    } catch (error) {
      console.error('Error creating new session:', error);
      setIsNewConversation(false);
      toast({
        title: "Failed to start new conversation",
        description: "Please try again",
        variant: "destructive",
      });
    }
  };

  const shareSession = () => {
    if (!sessionRef.current) return false;
    
    const sessionId = sessionRef.current.getSessionId();
    if (!sessionId) return false;

    const url = new URL(window.location.href);
    url.searchParams.set('session', sessionId);
    
    return url.toString();
  };

  useEffect(() => {
    initSession();
    return () => sessionRef.current?.cleanup();
  }, []);

  return { 
    messages, 
    isLoading, 
    isInitializing, 
    isNewConversation, 
    error, 
    isSharedSession, 
    sendMessage, 
    clearChat, 
    shareSession 
  };
}
